import React, { useEffect, useState } from "react";
import { Paper, Stepper, Step, StepLabel, Alert, Box, Typography, Card, Divider, Chip } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import axios from "axios";
import {
  showToast,
  WEBSERVICE,
  ACCREDITEDKYCWEBSERVICE,
} from "../../CoreDataService";
import AccreditedInvestorInfo from "./AccreditedInvestorInfo";
import AccreditedFinancialInfo from "./AccreditedFinancialInfo";
import AccreditedDocumentUpload from "./AccreditedDocumentUpload";
import AccreditedReview from "./AccreditedReview";
import AccreditedStatus from "./AccreditedStatus";
import {
  ACCREDITATION_TYPES,
} from "./utils";

const steps = [
  "Provide Details",
  "Upload Documents",
  "Review & Submit",
];

const AccreditedKYC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [tier2Status, setTier2Status] = useState(null);
  const [accreditedStatusData, setAccreditedStatusData] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isRejectedStatus, setIsRejectedStatus] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [thresholdsData, setThresholdsData] = useState(null);
  const [selectedCertification, setSelectedCertification] = useState("NONE");

  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");
  const theme = localStorage.getItem("theme");

  // Dark mode colors
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    card: "#1a1a1a",
    text: "#e0e0e0",
    textSecondary: "#aaa",
    border: "#333",
    primary: "#0d6efd",
    success: "#28a745",
    info: "#0dcaf0",
    warning: "#ffc107",
    error: "#dc3545",
    stepActive: "#0d6efd",
    stepInactive: "#333",
    stepTextActive: "#0d6efd",
    stepTextInactive: "#666",
  };

  // Light mode colors
  const lightColors = {
    background: "#f8f9fa",
    paper: "#ffffff",
    card: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    border: "#dee2e6",
    primary: "#1976d2",
    success: "#2e7d32",
    info: "#0288d1",
    warning: "#ed6c02",
    error: "#d32f2f",
    stepActive: "#1976d2",
    stepInactive: "#e0e0e0",
    stepTextActive: "#1976d2",
    stepTextInactive: "#757575",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  useEffect(() => {
    initializeApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper functions for income ID conversion
  const getIncomeFromId = (id) => {
    const incomeRanges = {
      1: 10000,
      2: 25000,
      3: 50000,
      4: 100000,
      5: 250000,
      6: 1000000,
      7: 10000000,
    };
    return incomeRanges[id] || 0;
  };

  const getIncomeRangeFromId = (id) => {
    const incomeRanges = {
      1: { min: 0, max: 10000 },
      2: { min: 10000, max: 25000 },
      3: { min: 25000, max: 50000 },
      4: { min: 50000, max: 100000 },
      5: { min: 100000, max: 250000 },
      6: { min: 250000, max: 1000000 },
      7: { min: 1000000, max: 10000000 }, // No upper limit
    };
    return incomeRanges[id] || { min: 0, max: 0 };
  };

  // ─── Initialize: Check Tier 2 status and get user details ───
  const initializeApplication = async () => {
    setCheckingStatus(true);
    try {
      // Step 1: Check Tier 2 status
      const tier2Status = await checkTier2Status();

      // Step 2: If Tier 2 verified, get user details
      if (tier2Status === "1") {
        await getUserDetails();
      }
    } catch (error) {
      console.error("Initialization error:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  // ─── Step 1: Check Tier 2 status via GetUserAppSettings ───
  const checkTier2Status = async () => {
    try {
      const response = await axios.post(
        `${WEBSERVICE}/user/GetUserAppSettings`,
        JSON.stringify({ uuid }),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${token}`,
          },
        }
      );

      const result = response.data;

      if (result.error.error_data !== 0) {
        showToast("error", result.error.error_msg);
        return null;
      }

      const userDocsStatus = result.userAppSettingsResult.user_docs_status;
      setTier2Status(userDocsStatus);
      return userDocsStatus;
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.error?.error_msg ||
        "Failed to check verification status"
      );
      return null;
    }
  };

  // ─── Step 2: Get user details ───
  const getUserDetails = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}/user/GetUserDetails`,
        { uuid },
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.error?.error_data === 0) {
        const userData = response?.data?.userResult;
        setUserDetails(userData);

        // Step 3: Check accreditation status
        const userId = userData?.user_id || userData?.userId || userData?.id;
        if (userId) {
          await checkAccreditedStatus(userId, userData);
        }
      } else {
        showToast("error", response?.data?.error?.error_msg);
      }
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.error?.error_msg ||
        "Failed to fetch user details"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Step 3: Check accreditation status ───
  const checkAccreditedStatus = async (userId, userData) => {
    try {
      const response = await axios.get(
        `${ACCREDITEDKYCWEBSERVICE}/accreditation/status/${userId}`,
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        const statusData = response.data.data;

        // Handle case where no application exists (data is null)
        if (!statusData) {
          // No existing application – check eligibility for new application
          try {
            await checkEligibility(userId, userData);
            await fetchThresholds();
            setShowForm(true);
            setIsRejectedStatus(false);
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
            showToast(
              "error",
              eligibilityError?.response?.data?.message || "Failed to check eligibility"
            );
          }
          return;
        }

        // Application exists - process based on status
        setAccreditedStatusData(statusData);
        setApplicationId(statusData.applicationId);

        // Determine if user can apply / should see form
        const status = statusData.status;
        const reapplyStatuses = [
          "CANCELLED",
        ];

        // Special handling for REJECTED status - directly show Document Upload
        // Call checkEligibility for all data EXCEPT accreditation type (taken from status API)
        if (status === "REJECTED") {
          setIsRejectedStatus(true);
          setShowForm(true);
          setActiveStep(1); // Directly go to Document Upload step

          // Call checkEligibility to populate income, net worth, and other data
          try {
            await checkEligibility(userId, userData);
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
          }

          // Override ONLY the accreditation type from status data
          setFormData(prev => ({
            ...prev,
            applicationId: statusData.applicationId,
            accreditationType: statusData.accreditationType, // Take from status API
            verificationMethod: "DOCUMENT_UPLOAD"
          }));

          await fetchThresholds();

        } else if (reapplyStatuses.includes(status)) {
          // Allow reapplication – also fetch eligibility
          try {
            await checkEligibility(userId, userData);
            await fetchThresholds();
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
          }
          setShowForm(true);
          setIsRejectedStatus(false);

        } else if (status === "DRAFT") {
          // Allow continue editing
          setShowForm(true);
          setIsRejectedStatus(false);
          await fetchThresholds();
          // Optionally fetch eligibility for draft
          try {
            await checkEligibility(userId, userData);
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
          }

        } else if (status === "ADDITIONAL_INFO_REQUIRED") {
          // For ADDITIONAL_INFO_REQUIRED, similar to REJECTED
          setIsRejectedStatus(true);
          setShowForm(true);
          setActiveStep(1);

          // Call checkEligibility to populate income, net worth, and other data
          try {
            await checkEligibility(userId, userData);
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
          }

          // Override ONLY the accreditation type from status data
          setFormData(prev => ({
            ...prev,
            applicationId: statusData.applicationId,
            accreditationType: statusData.accreditationType, // Take from status API
            verificationMethod: "DOCUMENT_UPLOAD"
          }));

          await fetchThresholds();

        } else if (status === "EXPIRED") {
          // For EXPIRED, go directly to Document Upload (same flow as REJECTED)
          setIsRejectedStatus(true);
          setShowForm(true);
          setActiveStep(1);

          // Call checkEligibility to populate income, net worth, and other data
          try {
            await checkEligibility(userId, userData);
          } catch (eligibilityError) {
            console.error('Eligibility check failed:', eligibilityError);
          }

          // Override ONLY the accreditation type from status data
          setFormData(prev => ({
            ...prev,
            applicationId: statusData.applicationId,
            accreditationType: statusData.accreditationType,
            verificationMethod: "DOCUMENT_UPLOAD"
          }));

          await fetchThresholds();
        }
        // PENDING, UNDER_REVIEW, APPROVED – show status only (showForm remains false)
      }
    } catch (error) {
      // 404 or no application found – proceed with new application
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        try {
          await checkEligibility(userId, userData);
          await fetchThresholds();
          setShowForm(true);
          setIsRejectedStatus(false);
        } catch (eligibilityError) {
          console.error('Eligibility check failed:', eligibilityError);
          showToast(
            "error",
            eligibilityError?.response?.data?.message || "Failed to check eligibility"
          );
        }
      } else {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to check accreditation status"
        );
      }
    }
  };

  // ─── Step 4: Check eligibility ───
  const checkEligibility = async (userId, userData, certificationOverride = null) => {
    try {
      const annualIncomeId = userData?.userType === 1 ? userData?.annual_income || userData?.annualIncomeId || userData?.annualIncome || 0 : userData?.enterpriseUser?.revenue || 0;
      const incomeRange = getIncomeRangeFromId(annualIncomeId);
      const calculatedIncome = getIncomeFromId(annualIncomeId);
      const netWorthId = userData?.userType === 1 ? userData?.netWorthId || 0 : userData?.enterpriseUser?.companyNetWorth || 0
      const calculatedNetWorth = getIncomeFromId(parseInt(netWorthId));

      const companyAssets = getIncomeFromId(parseInt(userData?.enterpriseUser?.companyAssets || 0));

      console.log("Net Worth ID:", netWorthId, "Calculated Net Worth:", calculatedNetWorth);

      // Use certificationOverride if provided, otherwise use state value
      const effectiveCertification = certificationOverride !== null ? certificationOverride : selectedCertification;


      const eligibilityPayload = {
        userId: userId,
        userType : userData?.userType === 1 ? "INDIVIDUAL" : "ENTERPRISE",
        annualIncome: calculatedIncome,
        entityAnnualIncome: userData?.userType === 1 ? null : calculatedIncome,
        annualIncomeMin: incomeRange.min,
        annualIncomeMax: incomeRange.max,
        jointIncome: calculatedIncome, // Using same value if joint not available
        incomeCurrency: localStorage.getItem("homeCurrency") || "USD",
        netWorth: calculatedNetWorth,
        entityNetWorth: userData?.userType === 1 ? null : calculatedNetWorth,
        netWorthCurrency: localStorage.getItem("homeCurrency") || "USD",
        hasProfessionalCertification: effectiveCertification !== "NONE",
        certificationType: effectiveCertification !== "NONE" ? effectiveCertification : null,
        certificationNumber: userData?.certification_number || userData?.certificationNumber || null,
        certificationIssuer: userData?.certification_issuer || userData?.certificationIssuer || null,
        isEntity: userData?.userType === 2 ? true : false,
        entityName: userData?.userType === 2 ? userData?.enterpriseUser?.companyName || userData?.enterpriseUser?.company_name || null : null,
        entityType: userData?.userType === 2 ? ({
          "General Partnerships": "PARTNERSHIP",
          "Limited Liability Company (by members)": "LLC",
          "Limited Liability Company (sole member)": "LLC",
          "Statutory Corporation": "CORPORATION",
        }[userData?.enterpriseUser?.corporateStructure || userData?.enterpriseUser?.corporate_structure] || null) : null,
        entityAssets: userData?.userType === 1 ? null : companyAssets || 0,
        annualIncomeId: annualIncomeId,
        netWorthId: netWorthId,
      };

      const response = await axios.post(
        `${ACCREDITEDKYCWEBSERVICE}/accreditation/check-eligibility`,
        eligibilityPayload,
        {
          headers: {
            Authorization: `BEARER ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        const eligData = response.data.data;
        setEligibilityData(eligData);

        // Auto-set the recommended accreditation type from API
        if (eligData?.recommendedType) {
          setFormData((prev) => ({
            ...prev,
            accreditationType: eligData.recommendedType,
            verificationMethod: "DOCUMENT_UPLOAD", // Default verification method
            annualIncomeId: annualIncomeId,
            netWorthId: netWorthId,
            netWorth: calculatedNetWorth,
            annualIncome: calculatedIncome,
            annualIncomeRange: incomeRange,
          }));
        } else {
          // Ensure income data is in formData even if no recommended type
          setFormData((prev) => ({
            ...prev,
            annualIncomeId: annualIncomeId,
            netWorthId: netWorthId,
            netWorth: calculatedNetWorth,
            annualIncome: calculatedIncome,
            annualIncomeRange: incomeRange,
          }));
        }
      }
    } catch (error) {
      // Even if eligibility check fails, allow the user to proceed with manual selection
      console.error("Eligibility check failed:", error);
      showToast(
        "warning",
        "Unable to auto-check eligibility. You can still proceed with manual selection."
      );

      const annualIncomeId = userData?.annual_income || userData?.annualIncomeId || userData?.annualIncome || 0;
      const netWorthId = userData?.net_worth || userData?.netWorthId || userData?.netWorth || 0;
      const calculatedNetWorth = getIncomeFromId(parseInt(netWorthId));
      setFormData((prev) => ({
        ...prev,
        annualIncomeId: annualIncomeId,
        netWorthId: netWorthId,
        netWorth: calculatedNetWorth,
        annualIncome: getIncomeFromId(annualIncomeId),
        annualIncomeRange: getIncomeRangeFromId(annualIncomeId),
      }));
    }
  };

  // ─── Fetch Accreditation Thresholds ───
  const fetchThresholds = async () => {
    try {
      const response = await axios.get(
        `${ACCREDITEDKYCWEBSERVICE}/accreditation/thresholds?userType=${userDetails?.userType === 1 ? 'INDIVIDUAL' : 'ENTERPRISE'}&fiatCurrency=${localStorage.getItem("homeCurrency") || "USD"}`,
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        setThresholdsData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch thresholds:", error);
      // Don't show error toast, just log it - thresholds are optional
    }
  };

  // ─── Navigation & Data Handlers ───
  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFormDataUpdate = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleReapply = async () => {
    setAccreditedStatusData(null);
    setShowForm(true);
    setIsRejectedStatus(false);
    setActiveStep(0);
    setFormData({});

    // Re-fetch eligibility for new application
    const userId = userDetails?.user_id || userDetails?.userId || userDetails?.id;
    if (userId) {
      await checkEligibility(userId, userDetails);
    }
  };

  const handleCertificationChange = async (certification) => {
    setSelectedCertification(certification);

    // Re-run eligibility check with new certification selection
    const userId = userDetails?.user_id || userDetails?.userId || userDetails?.id;
    if (userId) {
      await checkEligibility(userId, userDetails, certification); // Pass certification directly
    }
  };

  const handleSubmitSuccess = () => {
    // Refresh status after successful submission
    const userId =
      userDetails?.user_id || userDetails?.userId || userDetails?.id;
    if (userId) {
      checkAccreditedStatus(userId, userDetails);
    }
    setShowForm(false);
    setIsRejectedStatus(false);
    setActiveStep(0);
  };

  // ─── Step Content Renderer ───
  const renderStepContent = (step) => {
    const userId =
      userDetails?.user_id || userDetails?.userId || userDetails?.id;

    switch (step) {
      case 0:
        return (
          <AccreditedFinancialInfo
            handleNext={handleNext}
            handleBack={handleBack}
            userDetails={userDetails}
            formData={formData}
            handleFormDataUpdate={handleFormDataUpdate}
            eligibilityData={eligibilityData}
            theme={theme}
            isRejected={isRejectedStatus}
          />
        );
      case 1:
        return (
          <AccreditedDocumentUpload
            handleNext={handleNext}
            handleBack={handleBack}
            userDetails={userDetails}
            formData={formData}
            handleFormDataUpdate={handleFormDataUpdate}
            theme={theme}
            isRejected={isRejectedStatus}
          />
        );
      case 2:
        return (
          <AccreditedReview
            handleBack={handleBack}
            userDetails={userDetails}
            formData={formData}
            setIsLoading={setIsLoading}
            userId={userId}
            onSubmitSuccess={handleSubmitSuccess}
            // Pass applicationId and isEditing props
            applicationId={formData.applicationId || accreditedStatusData?.applicationId}
            isEditing={!!accreditedStatusData}
            isRejected={isRejectedStatus} // Pass rejected status to Review component
            theme={theme}
            handleFormDataUpdate={handleFormDataUpdate}
          />
        );
      default:
        return "Unknown step";
    }
  };

  // ─── Custom Stepper Component for Dark Mode ───
  const CustomStepper = () => {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        mb: 4,
        mt: 3
      }}>
        {/* Progress line */}
        <Box sx={{
          position: 'absolute',
          top: '15px',
          left: '15%',
          right: '15%',
          height: '2px',
          backgroundColor: colors.border,
          zIndex: 1
        }} />

        {steps.map((label, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <Box sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              backgroundColor: index <= activeStep ? colors.primary : colors.stepInactive,
              color: index <= activeStep ? '#fff' : colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              mb: 1
            }}>
              {index + 1}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.8rem',
                color: index <= activeStep ? colors.stepTextActive : colors.stepTextInactive,
                fontWeight: index <= activeStep ? 'bold' : 'normal'
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // ─── RENDER: Loading ───
  if (checkingStatus) {
    return (
      <div className="content-wrapper pt-0 pl-3" style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        {isLoading && (
          <div className="loading style-2">
            <div className="loading-wheel"></div>
          </div>
        )}
        <div className="container-fluid">
          <Paper
            sx={{
              padding: 3,
              marginTop: 3,
              backgroundColor: colors.paper,
              color: colors.text,
              borderRadius: 2,
              boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h5" sx={{ color: colors.text, pb: 2, fontWeight: 600 }}>
              Accredited Investor Verification
            </Typography>
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography sx={{ color: colors.text }}>
                Checking verification status...
              </Typography>
            </Box>
          </Paper>
        </div>
      </div>
    );
  }

  // ─── RENDER: Tier 2 not verified ───
  if (tier2Status !== "1") {
    let statusMessage = "";
    let statusSeverity = "warning";

    if (tier2Status === "") {
      statusMessage =
        "You need to complete Tier 2 Identity Verification before you can apply for Accredited Investor status.";
      statusSeverity = "info";
    } else if (tier2Status === "0" || tier2Status === "2") {
      statusMessage =
        "Your Tier 2 Identity Verification is currently under review. Please wait for approval before proceeding with Accredited Investor verification.";
      statusSeverity = "warning";
    }

    return (
      <div className="content-wrapper pt-0 pl-3" style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        <div className="container-fluid">
          <Paper
            sx={{
              padding: 3,
              marginTop: 3,
              backgroundColor: colors.paper,
              color: colors.text,
              borderRadius: 2,
              boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h5" sx={{ color: colors.text, pb: 2, fontWeight: 600 }}>
              Accredited Investor Verification
            </Typography>
            <Alert severity={statusSeverity} sx={{ mb: 3, backgroundColor: statusSeverity === 'info' ? colors.info + '20' : colors.warning + '20' }}>
              <strong>Tier 2 Verification Required</strong>
              <Typography sx={{ marginTop: "8px", marginBottom: "0" }}>
                {statusMessage}
              </Typography>
            </Alert>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ color: colors.text, mb: 2 }}>
                Requirements:
              </Typography>
              <Box component="ol" sx={{ color: colors.text, pl: 2 }}>
                <Typography component="li">Complete Tier 1 verification (Basic Information)</Typography>
                <Typography component="li">Submit Tier 2 documents (ID Proof, Address Proof)</Typography>
                <Typography component="li">Wait for Tier 2 approval</Typography>
                <Typography component="li">Then proceed with Accredited Investor verification</Typography>
              </Box>
              <Typography sx={{ marginTop: "16px" }}>
                <a href="/identity-verification" style={{ color: colors.primary }}>
                  Go to Identity Verification →
                </a>
              </Typography>
            </Box>
          </Paper>
        </div>
      </div>
    );
  }

  // ─── RENDER: Existing accreditation status (not reapplying) ───
  if (accreditedStatusData && !showForm) {
    const status = accreditedStatusData.status;
    const reapplyStatuses = [
      "CANCELLED",
    ];

    return (
      <div className="content-wrapper pt-0 pl-3" style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        {isLoading && (
          <div className="loading style-2">
            <div className="loading-wheel"></div>
          </div>
        )}
        <div className="container-fluid">
          <Paper
            sx={{
              padding: 3,
              marginTop: 3,
              backgroundColor: colors.paper,
              color: colors.text,
              borderRadius: 2,
              boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h5" sx={{ color: colors.text, pb: 2, fontWeight: 600 }}>
              Accredited Investor Verification
            </Typography>
            <AccreditedStatus statusData={accreditedStatusData} />

            {reapplyStatuses.includes(status) && (
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <button
                  onClick={handleReapply}
                  className="btn btn-primary"
                  style={{
                    borderRadius: "1.25rem",
                    padding: "10px 30px",
                    backgroundColor: colors.primary,
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 'bold'
                  }}
                >
                  Submit New Application
                </button>
              </Box>
            )}
          </Paper>
        </div>
      </div>
    );
  }

  // ─── RENDER: Accredited KYC Form (Tier 2 verified, ready to apply) ───
  return (
    <div className="content-wrapper pt-0 pl-3" style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      {isLoading && (
        <div className="loading style-2">
          <div className="loading-wheel"></div>
        </div>
      )}
      <div className="container-fluid">
        <Paper
          sx={{
            padding: 3,
            marginTop: 3,
            backgroundColor: colors.paper,
            color: colors.text,
            borderRadius: 2,
            boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h5" sx={{ color: colors.text, pb: 2, fontWeight: 600 }}>
            Accredited Investor Verification
          </Typography>

          {/* Status Banner - Show when status is REJECTED, EXPIRED, or ADDITIONAL_INFO_REQUIRED */}
          {isRejectedStatus && (
            <Alert
              severity={accreditedStatusData?.status === "EXPIRED" ? "warning" : "error"}
              sx={{
                mb: 3,
                backgroundColor: accreditedStatusData?.status === "EXPIRED"
                  ? colors.warning + '20'
                  : colors.error + '20',
                border: `1px solid ${accreditedStatusData?.status === "EXPIRED" ? colors.warning : colors.error}40`
              }}
            >
              <strong>
                {accreditedStatusData?.status === "EXPIRED"
                  ? "Application Expired"
                  : "Application Rejected"}
              </strong>
              <Typography sx={{ marginTop: "8px", marginBottom: "0" }}>
                {accreditedStatusData?.status === "EXPIRED"
                  ? "Your accredited investor verification has expired. Please upload updated documents to re-verify your accreditation."
                  : "Your previous application was rejected. Please upload new/updated documents for reconsideration."}
                {accreditedStatusData?.rejectionReason && (
                  <>
                    <br />
                    <strong>Reason:</strong> {accreditedStatusData.rejectionReason}
                  </>
                )}
              </Typography>
            </Alert>
          )}

          {/* Tier 2 Verified Status */}
          <Card sx={{
            backgroundColor: colors.info + '10',
            border: `1px solid ${colors.info}30`,
            mb: 2,
            p: 2,
            borderRadius: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip
                label="Tier 2 Verified"
                size="small"
                sx={{
                  backgroundColor: colors.info,
                  color: theme === 'dark' ? '#000' : '#fff',
                  fontWeight: 'bold',
                  mr: 1
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: colors.text }}>
              Your identity has been verified. You can now proceed with Accredited Investor verification.
            </Typography>
          </Card>

          {/* Eligibility Status - Only show for non-rejected or new applications */}
          {!isRejectedStatus && eligibilityData && eligibilityData.eligible && (
            <Card sx={{
              backgroundColor: colors.success + '10',
              border: `1px solid ${colors.success}30`,
              mb: 2,
              p: 2,
              borderRadius: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle sx={{ color: colors.success, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ color: colors.success, fontWeight: 'bold' }}>
                  ✓ You Qualify for Accredited Investor Status
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: colors.text }}>
                {eligibilityData?.message || "Based on your profile, you meet the requirements for accredited investor verification."}
              </Typography>
            </Card>
          )}

          {!isRejectedStatus && eligibilityData && !eligibilityData.eligible && (
            <Alert severity="warning" sx={{
              mb: 2,
              backgroundColor: colors.warning + '20'
            }}>
              <strong>Eligibility Notice</strong>
              <Typography sx={{ marginTop: "8px", marginBottom: "0" }}>
                {eligibilityData?.message ||
                  "Based on your profile, you may not meet all requirements. You can still proceed with the application."}
              </Typography>
              {eligibilityData?.ineligibleReasons?.length > 0 && (
                <ul style={{ marginTop: "8px", marginBottom: "0" }}>
                  {eligibilityData.ineligibleReasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}

          {/* Income Verification Card - Only show for eligible non-rejected applications */}
          {/* {!isRejectedStatus && eligibilityData && eligibilityData.eligible && (
            <Card sx={{
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              mb: 3,
              p: 2,
              borderRadius: 2
            }}>
               <Typography variant="subtitle1" sx={{ color: colors.text, fontWeight: 'bold', mb: 1 }}>
                {ACCREDITATION_TYPES[eligibilityData.recommendedType]?.shortLabel || "Income-Based Verification"}
              </Typography>
              <Chip
                label="Recommended"
                size="small"
                sx={{
                  backgroundColor: colors.info,
                  color: theme === 'dark' ? '#000' : '#fff',
                  fontWeight: 'bold',
                  mb: 2
                }}
              /> 
              <Box sx={{
                backgroundColor: colors.paper,
                borderLeft: `3px solid ${colors.primary}`,
                p: 2,
                mb: 2,
                borderRadius: '0 4px 4px 0'
              }}>
                <Typography variant="body2" sx={{ color: colors.text }}>
                  Individual income {">"} $200,000 or joint income {">"} $300,000 in each of the 2 most recent years
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: colors.success, fontWeight: 'bold' }}>
                ✓ Your annual income of ${formData.annualIncome?.toLocaleString() || '1,500,000'} exceeds the $200,000 threshold
              </Typography>
            </Card>
          )} */}

          {/* Professional Certification Selection - Only show for Individual users (userType 1) */}
          {!isRejectedStatus && thresholdsData && userDetails?.userType === 1 && activeStep === 0  && (
            <Card sx={{
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              mb: 3,
              p: 2,
              borderRadius: 2
            }}>
              <Typography variant="subtitle1" sx={{ color: colors.text, fontWeight: 'bold', mb: 2 }}>
                Do you have any professional certification?
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                Select your professional certification if applicable
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 2,
                mt: 2
              }}>
                {/* None Option */}
                <Card
                  onClick={() => handleCertificationChange("NONE")}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: selectedCertification === "NONE"
                      ? `2px solid ${colors.primary}`
                      : `1px solid ${colors.border}`,
                    backgroundColor: selectedCertification === "NONE"
                      ? colors.primary + '10'
                      : colors.paper,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: theme === 'dark' ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: colors.text,
                      fontWeight: selectedCertification === "NONE" ? 'bold' : 'normal'
                    }}>
                      None
                    </Typography>
                    {selectedCertification === "NONE" && (
                      <CheckCircle sx={{ color: colors.primary, fontSize: 20 }} />
                    )}
                  </Box>
                </Card>

                {/* Certification Options */}
                {thresholdsData.validCertifications?.map((cert) => (
                  <Card
                    key={cert}
                    onClick={() => handleCertificationChange(cert)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: selectedCertification === cert
                        ? `2px solid ${colors.primary}`
                        : `1px solid ${colors.border}`,
                      backgroundColor: selectedCertification === cert
                        ? colors.primary + '10'
                        : colors.paper,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: theme === 'dark' ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{
                        color: colors.text,
                        fontWeight: selectedCertification === cert ? 'bold' : 'normal'
                      }}>
                        {cert.replace(/_/g, ' ')}
                      </Typography>
                      {selectedCertification === cert && (
                        <CheckCircle sx={{ color: colors.primary, fontSize: 20 }} />
                      )}
                    </Box>
                  </Card>
                ))}
              </Box>
            </Card>
          )}

          {/* Custom Stepper - Hide for rejected status since we go directly to step 1 */}
          {!isRejectedStatus && (
            <>
              <CustomStepper />
              <Divider sx={{ my: 3, borderColor: colors.border }} />
            </>
          )}

          {/* Form Section - Show appropriate title based on status */}
          <Box sx={{ mt: 2 }}>
            {!isRejectedStatus && (
              <>
                <Typography variant="h6" sx={{ color: colors.text, mb: 2 }}>
                  Provide Financial Details
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    color: colors.textSecondary,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  Please provide the required information for Individual/Joint Income verification.
                </Typography>
              </>
            )}
            {isRejectedStatus && activeStep === 1 && (
              <>
                <Typography variant="h6" sx={{ color: colors.text, mb: 2 }}>
                  Upload Supporting Documents
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
                  {accreditedStatusData?.status === "EXPIRED"
                    ? "Your accredited investor verification has expired. Please upload updated documents to re-verify your accreditation."
                    : "Your previous application was rejected. Please upload new/updated documents for reconsideration."}
                </Typography>
              </>
            )}
          </Box>

          {/* Step Content */}
          <Box sx={{ mt: 2 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Paper>
      </div>
    </div>
  );
};

export default AccreditedKYC;